<script setup lang="ts">
import { onMounted, ref, onUnmounted } from 'vue'
import { toast } from 'vue-sonner'
import { AreaChart, Search, RefreshCw, PlusIcon } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import {
  Dialog
} from '@/components/ui/dialog'
import aplosApi from '@/services/aplosApi'
import { getLocalizedDateTimeString, toTitleCase, toShortString } from '@/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { type Tenant } from '@/types'
import moment from 'moment'
import { useRouter } from 'vue-router'
import { useTenantStore } from '@/store/tenantStore'
import { Button } from '@/components/ui/button';
import Select from '../ui/select/Select.vue'
import SelectTrigger from '../ui/select/SelectTrigger.vue'
import SelectValue from '../ui/select/SelectValue.vue'
import SelectContent from '../ui/select/SelectContent.vue'
import SelectGroup from '../ui/select/SelectGroup.vue'
import SelectItem from '../ui/select/SelectItem.vue'
import { useUser } from '@/composables/useUser'
import Loading from '@/components/Loading.vue'
import { searchColorMap } from '@/utils/index'
import AppMenuBar from "@/components/Menu/AppMenuBar.vue";
const auth = useUser()




type Props = {
  displayLimit?: number
  search?: string
}

const columns = ref([
  {
    title: 'Tenant ID',
    dataIndex: 'id',
    key: 'id',

  },
  {
    title: 'Tenant Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (a: Tenant, b: Tenant): number => { return a.name.toLowerCase().localeCompare(b.name); },
  },
  {
    title: 'Tenant Status',
    dataIndex: 'status',
    key: 'status',
    filters: [],
    onFilter: (value: string , record:any) => record.status?.toLowerCase().startsWith(value),
  },
  {
    title: 'Subscription Type',
    dataIndex: 'subscription_type',
    key: 'subscription_type',
    filters: [],
    onFilter: (value: string, record: any) => record.subscription_type?.toLowerCase().startsWith(value),
  },
  {
    title: 'Subscription Status',
    dataIndex: 'subscription_status',
    key: 'subscription_status',
    filters: [],
    onFilter: (value: string , record:any) => record.subscription_status?.toLowerCase().startsWith(value),
  },
  {
    title: 'Max Users',
    dataIndex: 'user_allocation',
    key: 'user_allocation',

    onFilter: (value: string, record: any) => record.user_allocation?.toLowerCase().startsWith(value),

  },
  {
    title: 'Start Date',
    dataIndex: 'start_utc',
    key: 'start_utc',
    sorter: (a: Tenant, b: Tenant): number => moment(a.start_utc).diff(moment(b.start_utc)),

  },
  {
    title: 'End Date',
    dataIndex: 'end_utc',
    key: 'end_utc',
    sorter: (a: Tenant, b: Tenant): number => moment(a.end_utc).diff(moment(b.end_utc))
  }
]);
const props = defineProps<Props>()
const { tenantData, tenants: storedTenants } = useTenantStore();


const tenants = ref<Tenant[]>(
  // create an empty array of Tenant
  Array.from({ length: props.displayLimit || 10 }, () => ({}) as Tenant)
)
const originalTenants = ref<Tenant[]>([]);
const loading = ref(true)
const router = useRouter();
const filterOptions = ["all", "enabled", "disabled", "archived"];
const selectedFilter = ref(filterOptions[1]);

function parsingRawData(items: any[]) {
  items = items.map((item: any) => {

    try {
      if (item.subscription) {
        item.subscription_status = item.subscription?.status
        item.start_utc = item.subscription?.start_utc
        item.end_utc = item.subscription?.end_utc
        item.subscription_type = item.subscription?.type
        item.subscription_id = item.id
        item.user_allocation = item.subscription?.user_allocation
      }

    } catch (error) {
      console.error(error)
    }

    return item
  })
  return items;
}

function generateFilters(data:any, field:any) {
  const uniqueValues = [...new Set(data.map((item:any) => item[field]?.toLowerCase()))].filter(Boolean);
  return uniqueValues.map((value:any) => ({
    text: toTitleCase(value),
    value,
  }));
}



function load_tenant_screen(tenant_id: string | null | undefined) {

  if (!tenant_id){
    // todo toast message
    console.error(`bad tenant id: ${tenant_id}`)
    return
  }
  const path = `/app/administration/tenant-management/edit/${tenant_id}`

  router.push({
    path: path
  });


}

async function loadData() {
  loading.value = true
  try {

    await auth.initialize()

    const global_admin = auth.is_global_admin()

    if (!global_admin){
      // push them to a different route
      load_tenant_screen(auth.user.tenant_id)
      return
    }

    const tenant_id =  global_admin ? "" : auth.user.tenant_id;
    const { data } = await aplosApi.getTenants(tenant_id,selectedFilter.value)

    // parse raw data (configuration, times)
    if (data.Tenants) {
      data.items = parsingRawData(data.Tenants);
    } else if (data.tenants) {
      data.items = parsingRawData(data.tenants);
    } else if (data.Items) {
      data.items = parsingRawData(data.Items);
     } else if (data.record_type == 'tenant'){
      data.items = [data]

    } else {
      throw new Error("Response does not contain 'Tenants' or 'tenants' or 'Items'.");
    }
    data.items.sort((a: Tenant, b: Tenant) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    tenantData.data = data.items
    columns.value = columns.value.map((column:any) => {
      if (['status', 'subscription_type', 'subscription_status'].includes(column.key)) {
        return {
          ...column,
          filters: generateFilters(data.items, column.key),
        };
      }
      return column;
    });
    storedTenants.splice(0, data.items.length, ...data.items);

    originalTenants.value = data.items;
    tenants.value = props.displayLimit ? data.items.slice(0, props.displayLimit) : data.items;
  } catch (error) {
    console.error(error)
    toast.error('Failed to load Tenants')
  }finally{
    loading.value = false
  }
}

function handleSearch(event: Event) {
  let searchKeyword = (event.target as HTMLInputElement).value.toLowerCase();
  loading.value = true
  if (searchKeyword.length > 0) {
    const filterRecords = originalTenants.value.filter(tenant => {
      let tenantStatus = '';
      let name = tenant.name.toLowerCase();
      if (tenant.status) {
        tenantStatus = tenant.status.toLowerCase();
      }
      let subsType = tenant.subscription_type?.toLowerCase();
      let subsStatus = tenant.subscription_status?.toLowerCase();
      let subsMaxUsers = '';
      let start_date = ''
      let end_date = ''
      let tenant_id = ''

      tenant_id = tenant.id.toLocaleLowerCase()

      if (tenant.user_allocation) {
        subsMaxUsers = tenant.user_allocation.toString();
      }

      if (tenant.start_utc) {
        let localized = getLocalizedDateTimeString(tenant.start_utc).toLocaleLowerCase()
        start_date = localized
      }
      if (tenant.end_utc) {
        let localized = getLocalizedDateTimeString(tenant.end_utc).toLocaleLowerCase()
        end_date = localized
      }

      const fieldsToCheck = [name, tenantStatus, subsType,
        subsStatus, subsMaxUsers, start_date, end_date, tenant_id]
      return fieldsToCheck.some(field => field?.includes(searchKeyword))

    });
    tenants.value = props.displayLimit ? filterRecords.slice(0, props.displayLimit) : filterRecords;
  } else {
    tenants.value = props.displayLimit ? originalTenants.value.slice(0, props.displayLimit) : originalTenants.value;
  }
  loading.value = false
}
const handleNameClick = (tenant: any) => {
  load_tenant_screen(tenant.id)
};

onMounted(() => {
  loadData();

})
onUnmounted(() => {

})

async function handleRefresh() {

  await loadData();
}

const navButtons = ref([
  {
    icon: RefreshCw,
    onClick: handleRefresh,
    animate: loading,
    title: "",
    toolTip: "Refresh",
  },
  {
    icon: PlusIcon,
    title: "",
    toolTip: "Add Tenant",
    href: "/app/administration/tenant-management/new"
  },
]);
</script>

<template>
  <Loading v-if="loading || !auth.is_global_admin()"></Loading>
  <div v-else>
    <AppMenuBar :showSearch="true" :onSearch="handleSearch" :buttons="navButtons" />
    <div class="w-full">
      <div class="flex justify-end">
        <div class="w-[20%] flex gap-4">
          <p class="flex items-center">Results:</p>
          <Select v-model="selectedFilter">
            <SelectTrigger class="w-[100%]">
              <SelectValue :placeholder="selectedFilter" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem v-for="option in filterOptions" :key="option" :value="option">
                  {{ toTitleCase(option) }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Button variant="outline" @click="loadData">
            <Search class="size-4" />
          </Button>
        </div>
      </div>
    </div>
    <div class="mb-2"></div>

    <Dialog>
      <a-table
        :dataSource="tenants"
        :columns="columns"
        :pagination="!!!props.displayLimit && { showSizeChanger: tenants.length >= 20 }"
        class="overflow-x-auto"
        rowKey="id"
        :customRow="(record: any) => ({
        onClick: !loading ? () => handleNameClick(record) : null,
        class: !loading ? 'cursor-pointer' : ''
      })"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="loading">
            <Skeleton class="my-4 h-6 w-full" />
          </template>
          <template v-else-if="column.key === 'id'">
            <a-tooltip>
              <template #title>Tenant ID:{{ record.id }}</template>
              {{ toShortString(record.id, 10) }}
            </a-tooltip>
          </template>
          <template v-else-if="column.key === 'status'">
            <span role="status">
              <Badge :class="searchColorMap(record.status)">
                {{ toTitleCase(record?.status ?? "NA") }}
              </Badge>
            </span>
          </template>

          <template v-else-if="column.key === 'subscription_type'">
            <span role="subscription-type">
              {{ toTitleCase(record.subscription_type ?? "Not Available") }}
            </span>
          </template>

          <template v-else-if="column.key === 'subscription_status'">
            <span role="status">
              <Badge :class="searchColorMap(record.subscription_status)">
                {{ toTitleCase(record.subscription_status ?? "Not Available") }}
              </Badge>
            </span>
          </template>

          <template v-else-if="column.key === 'start_utc'">
            <span role="start-time" title="Start Date">
              {{ getLocalizedDateTimeString(record.start_utc) }}
            </span>
          </template>

          <template v-else-if="column.key === 'end_utc'">
            <span role="end-time" title="End Date">
              {{ getLocalizedDateTimeString(record.end_utc) }}
            </span>
          </template>
        </template>

        <template #emptyText>
          <div class="py-10">
            <AreaChart class="mx-auto size-14 text-muted-foreground" />
            <p class="my-5">Manage Tenants</p>
          </div>
        </template>
      </a-table>
    </Dialog>

    <a id="file-download" download class="sr-only" />
  </div>
</template>

<style>
span.anticon.anticon-search {
  margin: 0px 0 5px 0px;
}
</style>
